import FadeOutToBlack from "@src/features/libraryItem/course/FadeOutToBlack";
import useScrollEdges from "@src/features/libraryItem/hooks/useScrollEdges";
import { HeightAndWidth } from "@src/lib/hooks/useRefHeightAndWidth";
import { Variants, motion } from "framer-motion";
import React, { FC, useMemo } from "react";
import Spinner from "@src/lib/ui/Spinner";
import usePhonePreviewScaleFactor from "@src/lib/hooks/usePhonePreviewScaleFactor";
import { cn } from "@src/lib/utils/cn";
import { AgentWorking } from "@src/lib/ui/AgentWorking";
import useBuilderCanvasTools from "@src/features/libraryItem/course/hooks/useBuilderCanvasTools";

export const DEFAULT_PHONE_HEIGHT = 784 * 0.95;
export const DEFAULT_PHONE_WIDTH = 375 * 0.95;
export const DEFAULT_PHONE_VERTICAL_PADDING = 10;
export const PHONE_BORDER_RADIUS = 32;

type Props = {
  content?: React.ReactNode;
  isSelected: boolean;
  isHovered?: boolean;
  parentDimensions: HeightAndWidth;
  parentVerticalPadding: number;
  /** Exact scale for the phone; overrides fitting it into parentDimensions. */
  scale?: number;
  animationKey?: string;
  animationScrollDirection?: ScrollDirection;
  /**
   * Whether the user can scroll the screen's content. When false, wheel and
   * trackpad gestures pass through to the nearest scrollable ancestor (e.g. the
   * course builder canvas) instead of being captured by the screen.
   * Defaults to true.
   */
  scrollable?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  phoneWidth?: number;
  phoneHeight?: number;
  hideFadeOutToBlack?: boolean;
  loading?: boolean;
  style?: React.CSSProperties;
  showAiBorder?: boolean;
  agentWorkingActive?: boolean;
  agentWorkingLabel?: string;
  showBorder?: boolean;
};

export type ScrollDirection = "up" | "down" | "none";

const PhoneOutline: FC<Props> = (props) => {
  const canvasTools = useBuilderCanvasTools();
  const { showBorder = true } = props;
  const phoneHeight = props.phoneHeight ?? DEFAULT_PHONE_HEIGHT;
  const phoneWidth = props.phoneWidth ?? DEFAULT_PHONE_WIDTH;
  const fittedScale = usePhonePreviewScaleFactor(
    props.parentDimensions,
    props.parentVerticalPadding,
    phoneHeight,
    phoneWidth,
  );
  const scale = props.scale ?? fittedScale;
  const directionInt = useMemo(() => {
    if (props.animationScrollDirection === "up") return -1;
    if (props.animationScrollDirection === "down") return 1;
    return 0;
  }, [props.animationScrollDirection]);
  const verticalAnimationMagnitude = useMemo(() => phoneHeight, [phoneHeight]);
  const animationVariants: Variants = useMemo(
    () => ({
      enter: (direction: number) => {
        if (direction === 0)
          return {
            y: 0,
          };
        return {
          y: verticalAnimationMagnitude * direction,
        };
      },
      center: {
        zIndex: 1,
        y: 0,
      },
      exit: (direction: number) => {
        if (direction === 0)
          return {
            y: 0,
          };
        return {
          zIndex: 0,
          y: verticalAnimationMagnitude * -direction,
        };
      },
    }),
    [verticalAnimationMagnitude],
  );

  return (
    <div data-testid="phone-outline" className="flex">
      <AgentWorking
        active={!!props.agentWorkingActive}
        label={props.agentWorkingLabel}
        className="flex"
        style={{ borderRadius: PHONE_BORDER_RADIUS * scale }}
      >
        <div
          className="flex"
          style={{
            transform: `scale(${scale})`,
            transformOrigin: "top left",
            minWidth: phoneWidth * scale,
            maxWidth: phoneWidth * scale,
            height: phoneHeight * scale,
            minHeight: phoneHeight * scale,
            maxHeight: phoneHeight * scale,
            width: phoneWidth * scale,
          }}
        >
          <div
            className={cn(
              "flex flex-1 self-stretch overflow-hidden relative",
              showBorder && "bg-white shadow-large",
            )}
            style={{
              userSelect: props.disabled ? "none" : undefined,
              minWidth: phoneWidth,
              maxWidth: phoneWidth,
              height: phoneHeight,
              minHeight: phoneHeight,
              maxHeight: phoneHeight,
              width: phoneWidth,
              borderRadius: PHONE_BORDER_RADIUS,
              ...props.style,
            }}
            id={"phone-outline-outer"}
          >
            <motion.div
              key={props.animationKey}
              custom={directionInt}
              variants={animationVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={PHONE_OUTLINE_ANIMATION_TRANSITION}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                flexDirection: "column",
                alignSelf: "stretch",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {props.loading ? (
                <Spinner />
              ) : (
                <PhoneOutlineInner key={props.animationKey} {...props} />
              )}
            </motion.div>

            <div
              className={cn(
                "absolute inset-0 pointer-events-none z-[1000] transition-colors duration-200",
                props.isSelected &&
                  !props.showAiBorder &&
                  cn(
                    "border-4",
                    // The design-system focus colour ships with the canvas
                    // tools (Ff.LibraryItemOverviewTab).
                    canvasTools ? "border-state-focus" : "border-blue-6",
                  ),
                // A quiet edge on hover, so the screen under the pointer
                // reads as the one its ⋯ and media tools belong to.
                props.isHovered &&
                  !props.isSelected &&
                  !props.showAiBorder &&
                  !props.readOnly &&
                  "border-2 border-border-secondary",
                props.showAiBorder && "border-4 animate-ai-border",
              )}
              style={{
                borderRadius: PHONE_BORDER_RADIUS,
              }}
            ></div>
          </div>
        </div>
      </AgentWorking>
    </div>
  );
};

const PhoneOutlineInner: FC<Props> = (props) => {
  const { scrollable = true } = props;
  const { scrollViewRef, onScroll, isScrolledToBottom, isScrolledToTop } =
    useScrollEdges();
  const phoneHeight = props.phoneHeight ?? DEFAULT_PHONE_HEIGHT;
  return (
    <div className="relative flex flex-1 flex-col self-stretch">
      {props.hideFadeOutToBlack ? null : (
        <FadeOutToBlack show={!isScrolledToTop} position={"top"} />
      )}
      <div
        ref={scrollViewRef}
        className={cn(
          "relative flex flex-1 flex-col self-stretch [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:size-0",
          // `overscroll-y-contain` keeps a long screen from flinging the canvas
          // once its own content runs out.
          scrollable
            ? "overflow-y-auto overscroll-y-contain"
            : "overflow-hidden",
        )}
        onScroll={onScroll}
      >
        <div
          className="flex flex-col self-stretch"
          style={{ height: phoneHeight, minHeight: phoneHeight }}
        >
          {props.content}
        </div>
      </div>
      {props.hideFadeOutToBlack ? null : (
        <FadeOutToBlack show={!isScrolledToBottom} position={"bottom"} />
      )}
    </div>
  );
};

export default PhoneOutline;

export const PHONE_OUTLINE_ANIMATION_DURATION_SEC = 0.3;
export const PHONE_OUTLINE_ANIMATION_TRANSITION: React.ComponentProps<
  typeof motion.div
>["transition"] = {
  duration: PHONE_OUTLINE_ANIMATION_DURATION_SEC,
  ease: [0.4, 0, 0.2, 1],
};
